// moms-harness:anchor v2
// JIRA: LOGISPRD-14923
import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { skuAttributeQueryKeys, type SkuAttributeParams } from '../queries/useGetSkuAttributeList'
import { clearLastView, EMPTY_VIEW, nextSearchSeq, restorableView, saveLastView, type SkuAttributeView } from '../components/lastView'

interface Options {
  /** URL 에서 복원한 조회 조건 — 마운트 시 한 번 읽는다 */
  restoredParams?: SkuAttributeParams
  /** 조회 조건이 바뀔 때(조회·초기화) 메뉴 세션·URL 에 남긴다. 화면이 넘긴다 — 이 훅은 저장소를 모른다 */
  persistCondition: (params: SkuAttributeParams) => void
}

export interface ListView {
  /** 지금 목록 — 조건(`_seq` 포함)과 페이지. 목록 query key 가 된다 */
  view: SkuAttributeView
  /** [조회] — 같은 조건이어도 새 `_seq` 로 1페이지부터 다시 받는다 (1.0 동작) */
  onSearch: (params: SkuAttributeParams) => void
  /** 진입 자동조회 — 마지막 목록과 조건이 같고 캐시가 있으면 그대로 되살린다. 아니면 [조회]와 같다 */
  onInitialSearch: (params: SkuAttributeParams) => void
  onPageChange: (page: number) => void
  onReset: () => void
}

/**
 * MA18 목록의 조회 조건·페이지 상태 (LOGISPRD-14923).
 *
 * 화면이 다시 마운트돼도(다른 메뉴 탭에 다녀옴) 보던 목록을 다시 받지 않는 것이 요점이다. 마지막 목록은 `lastView`
 * 모듈에 두고, **그 페이지 캐시가 남아 있을 때만** 되살린다 — 탭을 닫았거나 보관 시간이 지나 캐시가 없으면 되살려도
 * 받아야 하므로, 옛 페이지 번호로 여는 대신 [조회]처럼 1페이지부터 연다.
 */
export const useListView = ({ restoredParams, persistCondition }: Options): ListView => {
  const queryClient = useQueryClient()
  const isCached = useCallback(
    (view: SkuAttributeView) => queryClient.getQueryData(skuAttributeQueryKeys.list(view.params, view.page)) !== undefined,
    [queryClient],
  )

  // 첫 렌더부터 되살린다 — 필터의 진입 자동조회를 기다리면 그 사이 빈 표가 한 번 그려진다
  const [view, setView] = useState<SkuAttributeView>(() => restorableView(restoredParams, isCached) ?? EMPTY_VIEW)

  // **빈 목록은 저장하지 않는다.** 마운트 직후 빈 상태를 저장하면, 필터가 화주 목록을 기다려 진입 조회를 늦게 부를 때
  // 되살릴 마지막 목록이 이미 지워져 있다. 비우는 것은 [초기화]가 직접 한다
  useEffect(() => {
    if (view !== EMPTY_VIEW) saveLastView(view)
  }, [view])

  const onSearch = useCallback(
    (params: SkuAttributeParams) => {
      persistCondition(params)
      setView({ params: { ...params, _seq: nextSearchSeq() }, page: 1 })
    },
    [persistCondition],
  )

  const onInitialSearch = useCallback(
    (params: SkuAttributeParams) => {
      const restorable = restorableView(params, isCached)
      if (restorable) setView(restorable)
      else onSearch(params)
    },
    [isCached, onSearch],
  )

  const onPageChange = useCallback((page: number) => setView((prev) => ({ ...prev, page })), [])

  const onReset = useCallback(() => {
    persistCondition({})
    // 초기화 뒤 재진입이 초기화 전 목록을 되살리지 않게 한다
    clearLastView()
    setView(EMPTY_VIEW)
  }, [persistCondition])

  return { view, onSearch, onInitialSearch, onPageChange, onReset }
}
